/**
 * DraftReportsCard — DSU draft reports pending approval.
 * Stage 3 UI extraction from ReportsPage.js
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Search, Check, BarChart3, Trash2 } from 'lucide-react';

const DraftReportsCard = ({
  activeReport,
  savedReports,
  loadingSaved,
  draftSearch, setDraftSearch,
  selectedReportIds, setSelectedReportIds,
  toggleSelectReport,
  handleBulkApprove,
  handleBulkDelete,
  onPreview,
}) => {
  if (activeReport !== 'daily-sale-updates') return null;

  const allDrafts = savedReports.filter(r => r.report_type === 'daily-sale-updates' && r.report_status === 'draft');
  if (!allDrafts.length && !loadingSaved) return null;

  const q = draftSearch.toLowerCase();
  const drafts = allDrafts
    .filter(r => !q || (r.report_name || '').toLowerCase().includes(q) || (r.crm_entity_name || '').toLowerCase().includes(q) || (r.saved_at || '').includes(q))
    .sort((a, b) => {
      // 1. Generation date — most recent first (desc)
      const dateA = (a.saved_at || a.report_date || '').slice(0, 10);
      const dateB = (b.saved_at || b.report_date || '').slice(0, 10);
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      // 2. Sale name (alphabetical) — extracted from report_name "Daily Update (SALE) - HORSE"
      const extractSale = n => { const m = (n || '').match(/\(([^)]+(?:\([^)]*\))?[^)]*)\)/); return m ? m[1].toLowerCase() : (n||'').toLowerCase(); };
      const saleA = extractSale(a.report_name);
      const saleB = extractSale(b.report_name);
      if (saleA !== saleB) return saleA.localeCompare(saleB);
      // 3. Horse name / "All Horses" (alphabetical)
      const extractHorse = n => { const parts = (n || '').split(' - '); return (parts.length > 1 ? parts.slice(1).join(' - ') : '').toLowerCase(); };
      return extractHorse(a.report_name).localeCompare(extractHorse(b.report_name));
    });
  const selectedDraftIds = selectedReportIds.filter(id => allDrafts.some(d => d.id === id));

  return (
    <Card className="border-amber-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
              Draft Reports
            </CardTitle>
            {!loadingSaved && <span className="text-xs text-muted-foreground">({allDrafts.length})</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedDraftIds.length > 0 && (
              <>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleBulkApprove}>
                  <Check className="w-3 h-3 mr-1" /> Approve ({selectedDraftIds.length})
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleBulkDelete}>
                  <Trash2 className="w-3 h-3 mr-1" /> Delete ({selectedDraftIds.length})
                </Button>
              </>
            )}
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input className="pl-9 h-8 text-sm" placeholder="Search drafts..." value={draftSearch} onChange={e => setDraftSearch(e.target.value)} />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loadingSaved ? (
          <div className="flex justify-center py-6"><BarChart3 className="w-5 h-5 animate-spin" /></div>
        ) : drafts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No draft reports{q ? ' matching your search' : ''}.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <input type="checkbox" className="w-3.5 h-3.5"
                    checked={drafts.length > 0 && drafts.every(r => selectedReportIds.includes(r.id))}
                    onChange={() => {
                      const allSel = drafts.every(r => selectedReportIds.includes(r.id));
                      setSelectedReportIds(allSel ? [] : drafts.map(r => r.id));
                    }} />
                </TableHead>
                <TableHead>Sale</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Generated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drafts.map(r => (
                <TableRow key={r.id} className="hover:bg-amber-50/50">
                  <TableCell onClick={e => { e.stopPropagation(); toggleSelectReport(r.id); }}>
                    <input type="checkbox" className="w-3.5 h-3.5" checked={selectedReportIds.includes(r.id)} readOnly />
                  </TableCell>
                  <TableCell className="font-medium cursor-pointer text-sm" onClick={() => onPreview(r)}>{r.report_name}</TableCell>
                  <TableCell className="text-sm cursor-pointer" onClick={() => onPreview(r)}>{r.crm_entity_name || '-'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground cursor-pointer" onClick={() => onPreview(r)}>
                    {r.saved_at ? new Date(r.saved_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default DraftReportsCard;
